import { ShieldCheck, ShieldAlert } from "lucide-react";

type EscrowStatus = "UNFUNDED" | "LOCKED" | "RELEASED";

interface EscrowGuaranteeBadgeProps {
  status: EscrowStatus;
  paymentProvider?: string | null;
  className?: string;
}

/**
 * Buyer-facing escrow trust badge. When the escrow is actually funded
 * (LOCKED/RELEASED) it shows the green "Escrow Guaranteed" guarantee. When the
 * job is still UNFUNDED there is nothing held yet, so we must NOT claim a
 * guarantee — it renders a neutral "not yet funded" state instead.
 */
export function EscrowGuaranteeBadge({ status, paymentProvider, className }: EscrowGuaranteeBadgeProps) {
  const isCard = paymentProvider === "STRIPE";
  const rail = isCard ? "Card hold" : "USDC escrow";
  const funded = status !== "UNFUNDED";

  const copy: Record<EscrowStatus, string> = {
    UNFUNDED: isCard
      ? "Not funded yet. Once the card hold is placed, funds are locked until your acceptance criteria pass — refunded if rejected."
      : "Not funded yet. Once escrow is funded, funds are locked until your acceptance criteria pass — refunded if rejected.",
    LOCKED: "Payment held in escrow; released only on your approval or after the auto-approval window",
    RELEASED: "Released to agent. Complete.",
  };

  const Icon = funded ? ShieldCheck : ShieldAlert;
  const title = funded ? "Escrow Guaranteed" : "Escrow Not Yet Funded";

  // Emerald for a real guarantee, neutral muted for the unfunded state.
  const wrap = funded
    ? "border-emerald-500/20 bg-emerald-500/[0.06]"
    : "border-divider bg-muted/30";
  const iconWrap = funded ? "bg-emerald-500/15" : "bg-muted";
  const iconColor = funded ? "text-emerald-400" : "text-muted-foreground";
  const titleColor = funded ? "text-emerald-400" : "text-foreground";
  const railColor = funded ? "text-emerald-400/60" : "text-muted-foreground";

  return (
    <div className={`rounded-xl border ${wrap} p-4 ${className ?? ""}`}>
      <div className="flex items-center gap-2">
        <div className={`h-8 w-8 rounded-full ${iconWrap} flex items-center justify-center shrink-0`}>
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </div>
        <div className="leading-tight">
          <p className={`text-sm font-bold ${titleColor}`}>{title}</p>
          <p className={`text-[10px] font-mono uppercase tracking-wider ${railColor}`}>{rail}</p>
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{copy[status]}</p>
    </div>
  );
}
